import type { FC } from 'react'

import { Fragment } from 'react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'
import { YesNo } from '@/components/kit/yes-no'
import { featureGroups, plans } from '@/constants/pricing'

export const FeatureTable: FC = () => {
    return (
        <section
            id='compare-plans'
            aria-labelledby='compare-plans-title'
            className='mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28'
        >
            <div className='grid gap-8 lg:grid-cols-12 lg:items-end'>
                <div className='lg:col-span-7'>
                    <SectionLabel>Side by side</SectionLabel>
                    <SplitText
                        as='h2'
                        id='compare-plans-title'
                        lines={['The whole forecast,', 'line by line']}
                        italic='forecast'
                        className='mt-5 text-[clamp(2.5rem,5.6vw,5.25rem)] font-bold leading-[0.9] tracking-[-0.04em]'
                    />
                </div>
                <Reveal
                    as='p'
                    delay={150}
                    className='text-lg leading-relaxed text-stone lg:col-span-5'
                >
                    All three plans include the same team, tools and standards. The difference is volume, speed and how much of your social we take off your plate.
                </Reveal>
            </div>

            <Reveal
                delay={150}
                className='mt-12'
            >
                <div className='overflow-x-auto rounded-[1.75rem] border border-black/5 bg-paper md:rounded-[2rem]'>
                    <table className='w-full min-w-[680px] border-collapse text-left'>
                        <caption className='sr-only'>Features included in the Starter, Growth and Scale plans</caption>
                        <thead>
                            <tr>
                                <th
                                    scope='col'
                                    className='w-[34%] px-5 py-6 md:px-8'
                                >
                                    <span className='sr-only'>Feature</span>
                                </th>
                                {plans.map(plan => (
                                    <th
                                        key={plan.id}
                                        scope='col'
                                        className={cn('px-4 py-6 text-center md:px-6', plan.highlighted && 'bg-ember/[0.07]')}
                                    >
                                        <span className='block font-heading text-xl font-bold tracking-[-0.04em]'>{plan.name}</span>
                                        {plan.highlighted && (
                                            <span className='mt-1 block font-mono text-[10px] uppercase tracking-[0.14em] text-stone'>Most booked</span>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {featureGroups.map(group => (
                                <Fragment key={group.group}>
                                    <tr className='border-t border-ink/[0.07]'>
                                        <th
                                            scope='colgroup'
                                            colSpan={4}
                                            className='bg-sand/50 px-5 py-3 font-mono text-[11px] font-normal uppercase tracking-[0.16em] text-stone md:px-8'
                                        >
                                            {group.group}
                                        </th>
                                    </tr>
                                    {group.rows.map(row => (
                                        <tr
                                            key={row.label}
                                            className='border-t border-ink/[0.07]'
                                        >
                                            <th
                                                scope='row'
                                                className='px-5 py-4 text-[15px] font-medium md:px-8'
                                            >
                                                {row.label}
                                            </th>
                                            {row.values.map((value, index) => (
                                                <td
                                                    key={plans[index].id}
                                                    className={cn('px-4 py-4 text-center md:px-6', plans[index].highlighted && 'bg-ember/[0.07]')}
                                                >
                                                    <YesNo
                                                        value={value}
                                                        className={typeof value === 'string' ? 'text-sm font-medium text-ink md:text-[15px]' : 'size-8'}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Reveal>
        </section>
    )
}
